// Inductions workspace — per-client induction templates, site-specific content and
// the sign-on / attendance QR. PR staff manage every client (via a client selector);
// client_admin / site_manager manage their own client only.
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck, Plus, Copy, Trash2, Star, Power, Pencil, ChevronUp, ChevronDown,
    X, Loader2, Building, Image as ImageIcon, QrCode, MapPin, AlertTriangle, RefreshCw,
    Upload, FileText, Type, ArrowUp, ArrowDown, Printer, Sparkles, CheckCircle, Lock,
} from "lucide-react";
import { api, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteQRPoster from "@/components/SiteQRPoster";
import AssemblyPointsEditor from "@/components/onsite/AssemblyPointsEditor";
import Pill from "@/components/Pill";

const BRAND = "var(--brand-accent, #C75A1B)";
const INTERNAL = new Set(["director", "administrator", "consultant"]);
const FIELD_TYPES = [
    { v: "instruction", l: "Instruction / rule" },
    { v: "yes_no", l: "Yes / No" },
    { v: "text", l: "Short text" },
    { v: "textarea", l: "Paragraph" },
    { v: "dropdown", l: "Dropdown" },
    { v: "signature", l: "Signature" },
    { v: "photo", l: "Photo / evidence" },
];

export default function InductionsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isInternal = INTERNAL.has(user?.role);
    const isSiteManager = user?.role === "site_manager";
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState(isInternal ? "" : (user?.client_id || (user?.client_ids || [])[0] || ""));
    const [tab, setTab] = useState(searchParams.get("tab") || "templates");
    const deepLinkFilter = searchParams.get("project_id") ? {
        projectId: searchParams.get("project_id"), dateFrom: searchParams.get("date_from"), dateTo: searchParams.get("date_to"),
    } : null;

    useEffect(() => {
        if (isInternal) api.get("/clients").then((r) => {
            setClients(r.data || []);
            if (!clientId && (r.data || []).length) setClientId(r.data[0].id);
        }).catch(() => {});
    }, [isInternal]);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-5 pb-10" data-testid="inductions-page">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: BRAND }}><ShieldCheck size={20} /></div>
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">Inductions</h1>
                    <p className="text-[12.5px] text-[#6F6A64]">Induction templates, site-specific rules and sign-on / attendance QR.</p>
                </div>
                {isInternal && (
                    <select data-testid="inductions-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
                        className="h-10 px-3 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA", minWidth: 220 }}>
                        <option value="">— Select a client —</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
            </div>

            <div className="flex items-center gap-1 border-b" style={{ borderColor: "#E5E0DA" }}>
                {[["templates", "Templates"], ["toolbox", "Toolbox talks"], ["register", "Talk Register"], ["sites", "Site content"], ["qr", "Sign-on & attendance"], ["records", "Records & metrics"]].map(([id, label]) => (
                    <button key={id} data-testid={`inductions-tab-${id}`} onClick={() => setTab(id)}
                        className="px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors"
                        style={tab === id ? { borderColor: BRAND, color: BRAND } : { borderColor: "transparent", color: "#6F6A64" }}>
                        {label}
                    </button>
                ))}
            </div>

            {!clientId ? (
                <div className="text-center text-[13px] text-[#9A968F] py-16 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    Select a client to manage their inductions.
                </div>
            ) : tab === "templates" ? (
                <>
                    {isSiteManager && (
                        <button data-testid="inductions-build-banner" onClick={() => navigate("/app/inductions/build")}
                            className="w-full text-left rounded-[13px] p-4 flex items-center gap-3 border transition-colors hover:bg-[#FBEFE5]"
                            style={{ borderColor: BRAND, background: "#FBEFE5" }}>
                            <div className="w-9 h-9 rounded-lg grid place-items-center text-white flex-shrink-0" style={{ background: BRAND }}><ShieldCheck size={18} /></div>
                            <div className="flex-1">
                                <div className="text-[13.5px] font-semibold text-[#1F1F1F]">Build my site induction — guided setup</div>
                                <div className="text-[11.5px] text-[#6F6A64]">Clone a master, add assembly points, training certificates & contractor instructions, then get your QR poster.</div>
                            </div>
                        </button>
                    )}
                    <TemplatesTab clientId={clientId} clients={clients} isInternal={isInternal} />
                </>
            ) : tab === "toolbox" ? (
                <ToolboxTab clientId={clientId} clients={clients} isInternal={isInternal} user={user} />
            ) : tab === "register" ? (
                <ToolboxRegisterTab clientId={clientId} />
            ) : tab === "sites" ? (
                <SitesTab clientId={clientId} />
            ) : tab === "qr" ? (
                <QrTab clientId={clientId} clients={clients} />
            ) : (
                <RecordsTab clientId={clientId} deepLinkFilter={tab === "records" ? deepLinkFilter : null} />
            )}
        </div>
    );
}

/* ---------------- Templates ---------------- */
function TemplatesTab({ clientId, clients, isInternal }) {
    const [templates, setTemplates] = useState([]);
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDlg, setNewDlg] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get(`/inductions/templates?client_id=${clientId}`),
            api.get("/inductions/masters"),
        ]).then(([t, m]) => { setTemplates(t.data || []); setMasters(m.data || []); })
            .catch(() => {}).finally(() => setLoading(false));
    }, [clientId]);
    useEffect(load, [load]);

    async function setDefault(t) {
        try { await api.patch(`/inductions/templates/${t.id}`, { is_default: true }); load(); }
        catch { toast.error("Failed"); }
    }
    async function toggleActive(t) {
        try { await api.patch(`/inductions/templates/${t.id}`, { status: t.status === "published" ? "draft" : "published" }); load(); }
        catch { toast.error("Failed"); }
    }
    async function remove(t) {
        if (!window.confirm(`Delete induction template "${t.name}"?`)) return;
        try { await api.delete(`/inductions/templates/${t.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Failed"); }
    }
    async function cloneTo(t) {
        const others = clients.filter((c) => c.id !== clientId);
        if (!others.length) { toast.error("No other client to clone to"); return; }
        const name = window.prompt(`Clone "${t.name}" to which client? Type the client name exactly:\n` + others.map((c) => c.name).join("\n"));
        const target = others.find((c) => c.name.toLowerCase() === (name || "").trim().toLowerCase());
        if (!target) { if (name) toast.error("Client name not matched"); return; }
        try { await api.post(`/inductions/templates/${t.id}/clone`, { target_client_id: target.id }); toast.success(`Cloned to ${target.name}`); }
        catch { toast.error("Clone failed"); }
    }
    async function duplicate(t) {
        try {
            const r = await api.post("/inductions/templates", { client_id: clientId, name: `${t.name} (copy)`, clone_from: t.id });
            toast.success("Duplicated — edit your copy"); load(); setEditing(r.data);
        } catch { toast.error("Duplicate failed"); }
    }

    if (loading) return <Loading />;
    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <button data-testid="induction-new-template" onClick={() => setNewDlg(true)}
                    className="text-[12.5px] font-semibold rounded-full px-3.5 py-2 text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    <Plus size={14} /> New induction
                </button>
            </div>
            {templates.length === 0 ? (
                <Empty msg="No induction templates yet. Create one — start blank or clone a Priority Risk master." />
            ) : (
                <div className="grid md:grid-cols-2 gap-3" data-testid="induction-templates-list">
                    {templates.map((t) => (
                        <div key={t.id} data-testid={`induction-template-${t.id}`} className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[14px] font-semibold text-[#1F1F1F] truncate">{t.name}</span>
                                        {t.is_default && <span data-testid={`induction-default-${t.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FBEFE5", color: BRAND }}>Default</span>}
                                        {t.status !== "published" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0EDE8] text-[#9A968F]">Draft</span>}
                                    </div>
                                    {t.description && <p className="text-[12px] text-[#6F6A64] mt-1 line-clamp-2">{t.description}</p>}
                                    <p className="text-[11px] text-[#9A968F] mt-1">{(t.sections || []).length} sections</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-3">
                                <TBtn testid={`induction-edit-${t.id}`} icon={Pencil} label="Edit" onClick={() => setEditing(t)} />
                                {!t.is_default && <TBtn testid={`induction-setdefault-${t.id}`} icon={Star} label="Set default" onClick={() => setDefault(t)} />}
                                <TBtn testid={`induction-toggle-${t.id}`} icon={Power} label={t.status === "published" ? "Deactivate" : "Activate"} onClick={() => toggleActive(t)} />
                                <TBtn testid={`induction-duplicate-${t.id}`} icon={Copy} label="Duplicate" onClick={() => duplicate(t)} />
                                {isInternal && clients.length > 1 && <TBtn testid={`induction-clone-${t.id}`} icon={Copy} label="Clone to client" onClick={() => cloneTo(t)} />}
                                <TBtn testid={`induction-delete-${t.id}`} icon={Trash2} label="Delete" onClick={() => remove(t)} danger />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {newDlg && <NewTemplateDialog clientId={clientId} masters={masters} clientTemplates={templates}
                onClose={() => setNewDlg(false)} onCreated={(t) => { setNewDlg(false); load(); setEditing(t); }} />}
            {editing && <TemplateEditor template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function NewTemplateDialog({ clientId, masters, clientTemplates, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [cloneFrom, setCloneFrom] = useState("");
    const [busy, setBusy] = useState(false);
    async function create() {
        if (!name.trim()) { toast.error("Enter a name"); return; }
        setBusy(true);
        try {
            const r = await api.post("/inductions/templates", { client_id: clientId, name: name.trim(), clone_from: cloneFrom || null });
            toast.success("Induction created"); onCreated(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    }
    return (
        <Modal title="New induction template" onClose={onClose} testid="induction-new-dialog">
            <label className="block mb-3">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Name</span>
                <input data-testid="induction-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Visitor induction"
                    className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
            </label>
            <label className="block mb-4">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Start from</span>
                <select data-testid="induction-new-clone" value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">Blank template</option>
                    {masters.length > 0 && <optgroup label="Priority Risk masters">
                        {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                    {clientTemplates.length > 0 && <optgroup label="This client's inductions">
                        {clientTemplates.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                </select>
            </label>
            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="text-[13px] px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                <button data-testid="induction-new-create" onClick={create} disabled={busy}
                    className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create
                </button>
            </div>
        </Modal>
    );
}

function TemplateEditor({ template, onClose, onSaved }) {
    const [name, setName] = useState(template.name || "");
    const [description, setDescription] = useState(template.description || "");
    const [sections, setSections] = useState(JSON.parse(JSON.stringify(template.sections || [])));
    const [expanded, setExpanded] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const upd = (fn) => setSections((prev) => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next; });
    const addSection = () => upd((s) => s.push({ id: `s${Date.now()}`, title: "New section", order: s.length, fields: [], enabled: true }));
    const addCertificatesSection = () => upd((s) => s.push({
        id: `s${Date.now()}`, title: "Certificates & tickets", order: s.length, key: "certificates", enabled: true,
        fields: [{ id: `f${Date.now()}`, label: "Upload your training certificates & tickets", field_type: "cert_upload_freeform", required: true, enabled: true, options: [] }],
    }));
    const addContractorSection = () => upd((s) => s.push({
        id: `s${Date.now() + 1}`, title: "Instructions for Contractors", order: s.length, enabled: true,
        fields: [{ id: `f${Date.now() + 1}`, label: "Instructions for Contractors", field_type: "instruction", required: false, enabled: true, content: "", options: [] }],
    }));
    const moveSection = (i, d) => upd((s) => { const j = i + d; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; });
    const addField = (si, type) => upd((s) => s[si].fields.push({ id: `f${Date.now()}${Math.floor(Math.random() * 999)}`, label: type === "instruction" ? "" : "New field", field_type: type, required: false, enabled: true, content: "", options: [] }));
    const toggleExpand = (id) => setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    const toggleSectionEnabled = (si) => upd((s) => {
        const willEnable = s[si].enabled === false;
        s[si].enabled = willEnable;
        s[si].fields = (s[si].fields || []).map((f) => ({ ...f, enabled: willEnable }));
    });
    const toggleFieldEnabled = (si, fi) => upd((s) => { s[si].fields[fi].enabled = s[si].fields[fi].enabled === false; });

    async function save() {
        setBusy(true);
        try {
            await api.patch(`/inductions/templates/${template.id}`, { name, description, sections });
            toast.success("Saved"); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setBusy(false);
    }

    return (
        <Modal title="Edit induction" wide onClose={onClose} testid="induction-editor">
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <label className="block">
                    <span className="text-[12px] font-semibold text-[#1F1F1F]">Name</span>
                    <input data-testid="induction-editor-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
                </label>
                <label className="block">
                    <span className="text-[12px] font-semibold text-[#1F1F1F]">Description</span>
                    <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
                </label>
            </div>
            <p className="text-[11.5px] text-[#9A968F] -mt-1 mb-3">Untick a section to switch it off without deleting it — its content stays put, ready to turn back on later. Drop a section down to fine-tune (or switch off) individual questions.</p>

            <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1" data-testid="induction-editor-sections">
                {sections.map((sec, si) => {
                    const kept = sec.enabled !== false;
                    const isOpen = expanded.has(sec.id);
                    const fields = sec.fields || [];
                    const keptCount = fields.filter((f) => f.enabled !== false).length;
                    return (
                        <div key={sec.id} data-testid={`induction-editor-section-${sec.id}`} className="border rounded-xl p-3" style={{ borderColor: "#E5E0DA", background: kept ? "#fff" : "#FAF8F5" }}>
                            <div className="flex items-center gap-2 mb-2">
                                <input type="checkbox" data-testid={`induction-editor-section-check-${sec.id}`} checked={kept} onChange={() => toggleSectionEnabled(si)} className="w-4 h-4 flex-shrink-0" />
                                <input value={sec.title} onChange={(e) => upd((s) => { s[si].title = e.target.value; })}
                                    style={{ borderColor: "#E5E0DA", color: kept ? "#1F1F1F" : "#9A968F" }}
                                    className="flex-1 h-9 px-2.5 rounded-lg border text-[13px] font-semibold" />
                                <span className="text-[10.5px] text-[#9A968F] flex-shrink-0 whitespace-nowrap">{kept ? `${keptCount}/${fields.length}` : fields.length} item(s)</span>
                                <button data-testid={`induction-editor-section-expand-${sec.id}`} onClick={() => toggleExpand(sec.id)} className="text-[#9A968F] hover:text-[#1F1F1F]">
                                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                                <button onClick={() => moveSection(si, -1)} className="text-[#9A968F] hover:text-[#1F1F1F]"><ArrowUp size={14} /></button>
                                <button onClick={() => moveSection(si, 1)} className="text-[#9A968F] hover:text-[#1F1F1F]"><ArrowDown size={14} /></button>
                                <button onClick={() => upd((s) => s.splice(si, 1))} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                            </div>
                            {isOpen && (<>
                                <div className="space-y-2">
                                    {fields.map((f, fi) => {
                                        const fieldKept = f.enabled !== false;
                                        const isLocked = !!f.locked;
                                        return (
                                            <div key={f.id} className="flex items-start gap-2 bg-[#FCFBF9] rounded-lg p-2 border" style={{ borderColor: "#F0EDE8", opacity: kept ? 1 : 0.6 }}>
                                                <input type="checkbox" checked={fieldKept} disabled={!kept || isLocked} onChange={() => toggleFieldEnabled(si, fi)} className="w-3.5 h-3.5 mt-2.5 flex-shrink-0" />
                                                <span className="text-[10px] font-bold uppercase text-[#9A968F] w-16 pt-2">{f.field_type === "cert_upload_freeform" ? "Certificates" : (FIELD_TYPES.find((x) => x.v === f.field_type)?.l || f.field_type)}</span>
                                                <div className="flex-1 space-y-1.5">
                                                    {f.field_type === "instruction" ? (
                                                        <textarea value={f.content || ""} onChange={(e) => upd((s) => { s[si].fields[fi].content = e.target.value; })}
                                                            rows={2} placeholder="Rule / instruction text" className="w-full px-2.5 py-1.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                                                    ) : (
                                                        <input value={f.label || ""} onChange={(e) => upd((s) => { s[si].fields[fi].label = e.target.value; })}
                                                            placeholder="Question / label" className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                                                    )}
                                                    {f.field_type === "dropdown" && (
                                                        <input value={(f.options || []).join(", ")} disabled={isLocked} onChange={(e) => upd((s) => { s[si].fields[fi].options = e.target.value.split(",").map((x) => x.trim()).filter(Boolean); })}
                                                            placeholder="Options, comma separated" className="w-full h-9 px-2.5 rounded-lg border text-[12.5px] disabled:opacity-60" style={{ borderColor: "#E5E0DA" }} />
                                                    )}
                                                    {f.field_type !== "instruction" && (
                                                        <label className="inline-flex items-center gap-1.5 text-[11.5px] text-[#6F6A64]">
                                                            <input type="checkbox" checked={!!f.required} disabled={isLocked} onChange={(e) => upd((s) => { s[si].fields[fi].required = e.target.checked; })} /> Required
                                                        </label>
                                                    )}
                                                    {isLocked && (
                                                        <p className="text-[10.5px] text-[#9A968F] inline-flex items-center gap-1"><Lock size={10} /> Required by the Weekly Site Report — can't be switched off or deleted.</p>
                                                    )}
                                                </div>
                                                {isLocked ? (
                                                    <span className="text-[#9A968F] pt-1.5" title="Locked — can't be deleted"><Lock size={14} /></span>
                                                ) : (
                                                    <button onClick={() => upd((s) => s[si].fields.splice(fi, 1))} className="text-red-400 hover:text-red-600 pt-1.5"><X size={14} /></button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {FIELD_TYPES.map((t) => (
                                        <button key={t.v} onClick={() => addField(si, t.v)} className="text-[11px] px-2 py-1 rounded-full border text-[#6F6A64] hover:bg-[#FCFBF9]" style={{ borderColor: "#E5E0DA" }}>+ {t.l}</button>
                                    ))}
                                </div>
                            </>)}
                        </div>
                    );
                })}
                <button data-testid="induction-add-section" onClick={addSection} className="text-[12.5px] font-semibold px-3 py-2 rounded-lg border w-full" style={{ borderColor: "#E5E0DA", color: BRAND }}>+ Add section</button>
                <div className="flex flex-wrap gap-2">
                    <button data-testid="induction-add-certificates-section" onClick={addCertificatesSection} className="flex-1 text-[12.5px] font-semibold px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>+ Certificates section</button>
                    <button data-testid="induction-add-contractor-section" onClick={addContractorSection} className="flex-1 text-[12.5px] font-semibold px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>+ Contractor instructions</button>
                </div>
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t" style={{ borderColor: "#F0EDE8" }}>
                <button onClick={onClose} className="text-[13px] px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                <button data-testid="induction-editor-save" onClick={save} disabled={busy}
                    className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    {busy ? <Loader2 className="animate-spin" size={14} /> : null} Save
                </button>
            </div>
        </Modal>
    );
}

/* ---------------- Toolbox talks ---------------- */
function ToolboxTab({ clientId, clients, isInternal, user }) {
    const canManage = isInternal || user?.can_manage_forms !== false;
    const [talks, setTalks] = useState([]);
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDlg, setNewDlg] = useState(false);
    const [aiImportDlg, setAiImportDlg] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get(`/toolbox/templates?client_id=${clientId}`),
            api.get("/toolbox/masters"),
        ]).then(([t, m]) => { setTalks(t.data || []); setMasters(m.data || []); })
            .catch(() => {}).finally(() => setLoading(false));
    }, [clientId]);
    useEffect(load, [load]);

    async function setDefault(t) {
        try { await api.patch(`/toolbox/templates/${t.id}`, { is_default: true }); load(); }
        catch { toast.error("Failed"); }
    }
    async function toggleActive(t) {
        try { await api.patch(`/toolbox/templates/${t.id}`, { status: t.status === "published" ? "draft" : "published" }); load(); }
        catch { toast.error("Failed"); }
    }
    async function remove(t) {
        if (!window.confirm(`Delete toolbox talk "${t.name}"?`)) return;
        try { await api.delete(`/toolbox/templates/${t.id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Failed"); }
    }
    async function cloneTo(t) {
        const others = clients.filter((c) => c.id !== clientId);
        if (!others.length) { toast.error("No other client to clone to"); return; }
        const name = window.prompt(`Clone "${t.name}" to which client? Type the client name exactly:\n` + others.map((c) => c.name).join("\n"));
        const target = others.find((c) => c.name.toLowerCase() === (name || "").trim().toLowerCase());
        if (!target) { if (name) toast.error("Client name not matched"); return; }
        try { await api.post(`/toolbox/templates/${t.id}/clone`, { target_client_id: target.id }); toast.success(`Cloned to ${target.name}`); }
        catch { toast.error("Clone failed"); }
    }
    async function duplicate(t) {
        try {
            const r = await api.post("/toolbox/templates", { client_id: clientId, name: `${t.name} (copy)`, clone_from: t.id });
            toast.success("Duplicated — edit your copy"); load(); setEditing(r.data);
        } catch { toast.error("Duplicate failed"); }
    }

    if (loading) return <Loading />;
    return (
        <div className="space-y-3">
            <p className="text-[12.5px] text-[#6F6A64]">
                Build a library of toolbox talks (PPE, working at height, and any other topic) — each has its own write-up,
                shown to workers on the register before they sign. The register itself (name, company, signature) is the same for every talk.
            </p>
            {!canManage && (
                <p data-testid="toolbox-view-only-note" className="text-[12px] text-[#9A968F] bg-[#F5F3F0] border border-[#E5E0DA] rounded-lg p-2.5">
                    You have view-only access to Toolbox Talks. Ask your Priority Risk contact to enable "Manage site forms & Toolbox Talks" on your account.
                </p>
            )}
            {canManage && (
                <div className="flex justify-end gap-2">
                    <button data-testid="toolbox-ai-import" onClick={() => setAiImportDlg(true)}
                        className="text-[12.5px] font-semibold rounded-full px-3.5 py-2 text-white inline-flex items-center gap-1.5 bg-ai-grad">
                        <Sparkles size={14} /> Import with Beyond AI
                    </button>
                    <button data-testid="toolbox-new-talk" onClick={() => setNewDlg(true)}
                        className="text-[12.5px] font-semibold rounded-full px-3.5 py-2 text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                        <Plus size={14} /> New talk
                    </button>
                </div>
            )}
            {talks.length === 0 ? (
                <Empty msg="No toolbox talks yet. Create one — start blank, clone a Priority Risk master (e.g. PPE, Working at Height), or import with Beyond AI." />
            ) : (
                <div className="grid md:grid-cols-2 gap-3" data-testid="toolbox-talks-list">
                    {talks.map((t) => {
                        const hasBriefing = !!(t.briefing_body || t.briefing_document_id);
                        return (
                            <div key={t.id} data-testid={`toolbox-talk-${t.id}`} className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[14px] font-semibold text-[#1F1F1F] truncate">{t.name}</span>
                                            {t.is_default && <span data-testid={`toolbox-default-${t.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FBEFE5", color: BRAND }}>Default</span>}
                                            {t.status !== "published" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0EDE8] text-[#9A968F]">Draft</span>}
                                        </div>
                                        {t.description && <p className="text-[12px] text-[#6F6A64] mt-1 line-clamp-2">{t.description}</p>}
                                        <p className="text-[11px] mt-1 inline-flex items-center gap-1" style={hasBriefing ? { color: "#2E7D4F" } : { color: "#B33A2E" }}>
                                            {hasBriefing ? <FileText size={11} /> : <AlertTriangle size={11} />}
                                            {t.briefing_document_id ? "Briefing: uploaded document" : t.briefing_body ? "Briefing: written write-up" : "No briefing written yet"}
                                        </p>
                                    </div>
                                </div>
                                {canManage && (
                                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                                        <TBtn testid={`toolbox-edit-${t.id}`} icon={Pencil} label="Edit write-up" onClick={() => setEditing(t)} />
                                        {!t.is_default && <TBtn testid={`toolbox-setdefault-${t.id}`} icon={Star} label="Set default" onClick={() => setDefault(t)} />}
                                        <TBtn testid={`toolbox-toggle-${t.id}`} icon={Power} label={t.status === "published" ? "Deactivate" : "Activate"} onClick={() => toggleActive(t)} />
                                        <TBtn testid={`toolbox-duplicate-${t.id}`} icon={Copy} label="Duplicate" onClick={() => duplicate(t)} />
                                        {isInternal && clients.length > 1 && <TBtn testid={`toolbox-clone-${t.id}`} icon={Copy} label="Clone to client" onClick={() => cloneTo(t)} />}
                                        <TBtn testid={`toolbox-delete-${t.id}`} icon={Trash2} label="Delete" onClick={() => remove(t)} danger />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {newDlg && <NewTalkDialog clientId={clientId} masters={masters} clientTalks={talks}
                onClose={() => setNewDlg(false)} onCreated={(t) => { setNewDlg(false); load(); setEditing(t); }} />}
            {aiImportDlg && <ToolboxAiImportDialog clientId={clientId}
                onClose={() => setAiImportDlg(false)} onImported={() => { setAiImportDlg(false); load(); }} />}
            {editing && <TalkEditor talk={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

/* ---------------- Toolbox Talk Register (consolidated) ---------------- */
function ToolboxRegisterTab({ clientId }) {
    const [sites, setSites] = useState([]);
    const [projects, setProjects] = useState([]);
    const [entries, setEntries] = useState([]);
    const [talks, setTalks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [f, setF] = useState({ template_id: "", project_id: "", site_id: "", date_from: "", date_to: "" });

    useEffect(() => {
        if (!clientId) return;
        api.get(`/sites?client_id=${clientId}`).then((r) => setSites(r.data || [])).catch(() => setSites([]));
        api.get(`/projects?client_id=${clientId}`).then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    }, [clientId]);

    const params = useCallback(() => {
        const p = { client_id: clientId };
        if (f.template_id) p.template_id = f.template_id;
        if (f.project_id) p.project_id = f.project_id;
        if (f.site_id) p.site_id = f.site_id;
        if (f.date_from) p.date_from = f.date_from;
        if (f.date_to) p.date_to = f.date_to;
        return p;
    }, [clientId, f]);

    const load = useCallback(() => {
        if (!clientId) return;
        setLoading(true);
        api.get("/toolbox/register", { params: params() })
            .then((r) => { setEntries(r.data.entries || []); setTalks(r.data.talks || []); })
            .catch(() => setEntries([])).finally(() => setLoading(false));
    }, [clientId, params]);
    useEffect(load, [load]);

    async function generatePdf() {
        setPdfBusy(true);
        try {
            const res = await api.get("/toolbox/register/pdf", { params: params(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, "toolbox-talk-register.pdf");
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not generate PDF"); }
        setPdfBusy(false);
    }

    const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };
    const signedCount = entries.filter((e) => e.status === "Signed").length;

    return (
        <div className="space-y-4" data-testid="toolbox-register-tab">
            <p className="text-[12.5px] text-[#6F6A64]">
                Every attendee who signs a toolbox talk register (via the QR sign-on) appears here automatically — a single
                consolidated list of Talk, Attendee, Company, Client, Project, Date/Time and signature status for compliance evidence.
            </p>
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-end gap-2 flex-wrap">
                    <FilterField label="Talk">
                        <select data-testid="toolbox-register-filter-talk" value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All talks</option>
                            {talks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="Project">
                        <select data-testid="toolbox-register-filter-project" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All projects</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="Site">
                        <select data-testid="toolbox-register-filter-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">All sites</option>
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </FilterField>
                    <FilterField label="From">
                        <input data-testid="toolbox-register-filter-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }} />
                    </FilterField>
                    <FilterField label="To">
                        <input data-testid="toolbox-register-filter-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })}
                            className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }} />
                    </FilterField>
                    <button data-testid="toolbox-register-refresh" onClick={load} className="h-9 px-2.5 rounded-lg border text-[#6F6A64] hover:text-[#1F1F1F]" style={{ borderColor: "#E5E0DA" }} title="Refresh"><RefreshCw size={14} /></button>
                </div>
                <button data-testid="toolbox-register-generate-pdf" onClick={generatePdf} disabled={pdfBusy || loading}
                    className="text-[13px] font-bold rounded-full px-4 py-2.5 text-white inline-flex items-center gap-2 disabled:opacity-60" style={{ background: BRAND }}>
                    {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                    Generate PDF / Print Register
                </button>
            </div>

            {loading ? <Loading /> : entries.length === 0 ? (
                <Empty msg="No signed attendees match these filters yet." />
            ) : (
                <>
                    <p className="text-[11.5px] text-[#9A968F]">{entries.length} record{entries.length === 1 ? "" : "s"} · {signedCount} signed</p>
                    <div className="bg-white border rounded-[13px] overflow-hidden shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                        <table className="w-full text-[12.5px]" data-testid="toolbox-register-table">
                            <thead><tr className="text-left text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                <th className="px-4 py-2.5 font-semibold">Talk</th>
                                <th className="px-4 py-2.5 font-semibold">Attendee</th>
                                <th className="px-4 py-2.5 font-semibold">Company</th>
                                <th className="px-4 py-2.5 font-semibold">Client</th>
                                <th className="px-4 py-2.5 font-semibold">Project / Site</th>
                                <th className="px-4 py-2.5 font-semibold">Date / Time</th>
                                <th className="px-4 py-2.5 font-semibold">Status</th>
                            </tr></thead>
                            <tbody>
                                {entries.map((e, i) => (
                                    <tr key={`${e.instance_id}-${i}`} data-testid={`toolbox-register-row-${i}`} className="border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                        <td className="px-4 py-2.5 font-semibold text-[#1F1F1F]">{e.toolbox_talk}</td>
                                        <td className="px-4 py-2.5 text-[#1F1F1F]">{e.attendee}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.company}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.client_name}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{e.project_or_site}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{fmt(e.date)}</td>
                                        <td className="px-4 py-2.5"><Pill tone={e.status === "Signed" ? "green" : "amber"}>{e.status}</Pill></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9A968F]">{label}</span>
            {children}
        </div>
    );
}

function NewTalkDialog({ clientId, masters, clientTalks, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [cloneFrom, setCloneFrom] = useState("");
    const [busy, setBusy] = useState(false);
    async function create() {
        if (!name.trim()) { toast.error("Enter a name"); return; }
        setBusy(true);
        try {
            const r = await api.post("/toolbox/templates", { client_id: clientId, name: name.trim(), clone_from: cloneFrom || null });
            toast.success("Toolbox talk created"); onCreated(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    }
    return (
        <Modal title="New toolbox talk" onClose={onClose} testid="toolbox-new-dialog">
            <label className="block mb-3">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Name</span>
                <input data-testid="toolbox-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Manual handling"
                    className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
            </label>
            <label className="block mb-4">
                <span className="text-[12.5px] font-semibold text-[#1F1F1F]">Start from</span>
                <select data-testid="toolbox-new-clone" value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    <option value="">Blank talk</option>
                    {masters.length > 0 && <optgroup label="Priority Risk masters">
                        {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                    {clientTalks.length > 0 && <optgroup label="This client's talks">
                        {clientTalks.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </optgroup>}
                </select>
            </label>
            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="text-[13px] px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                <button data-testid="toolbox-new-create" onClick={create} disabled={busy}
                    className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create
                </button>
            </div>
        </Modal>
    );
}

function TalkEditor({ talk, onClose, onSaved }) {
    const [name, setName] = useState(talk.name || "");
    const [description, setDescription] = useState(talk.description || "");
    const [briefing, setBriefing] = useState(talk.briefing_body || "");
    const [docId, setDocId] = useState(talk.briefing_document_id || null);
    const [docFilename, setDocFilename] = useState(talk._briefing_filename || null);
    const [mode, setMode] = useState(talk.briefing_document_id ? "upload" : "type");
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [changed, setChanged] = useState(false); // upload/remove already persisted server-side — force a parent refresh on close either way

    function closeEditor() { changed ? onSaved() : onClose(); }

    async function uploadFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/toolbox/templates/${talk.id}/briefing`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDocId(r.data.briefing_document_id); setDocFilename(file.name); setBriefing(""); setChanged(true);
            toast.success("Briefing document uploaded");
        } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
        setUploading(false);
    }
    async function removeDoc() {
        try {
            await api.delete(`/toolbox/templates/${talk.id}/briefing`);
            setDocId(null); setDocFilename(null); setMode("type"); setChanged(true);
            toast.success("Document removed");
        } catch { toast.error("Failed"); }
    }

    async function save() {
        setBusy(true);
        try {
            const payload = { name, description };
            if (mode === "type") payload.briefing_body = briefing;
            await api.patch(`/toolbox/templates/${talk.id}`, payload);
            toast.success("Saved"); onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setBusy(false);
    }

    return (
        <Modal title="Edit toolbox talk" wide onClose={closeEditor} testid="toolbox-editor">
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <label className="block">
                    <span className="text-[12px] font-semibold text-[#1F1F1F]">Name</span>
                    <input data-testid="toolbox-editor-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
                </label>
                <label className="block">
                    <span className="text-[12px] font-semibold text-[#1F1F1F]">Description (internal use only)</span>
                    <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5" style={{ borderColor: "#E5E0DA" }} />
                </label>
            </div>

            <div className="mb-2">
                <span className="text-[12px] font-semibold text-[#1F1F1F]">Briefing — what workers read before they sign</span>
                <div className="flex items-center gap-1.5 mt-1.5 mb-2">
                    <button data-testid="toolbox-editor-mode-type" onClick={() => setMode("type")}
                        className="text-[11.5px] font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border"
                        style={mode === "type" ? { borderColor: BRAND, color: BRAND, background: "#FBEFE5" } : { borderColor: "#E5E0DA", color: "#6F6A64" }}>
                        <Type size={12} /> Write it
                    </button>
                    <button data-testid="toolbox-editor-mode-upload" onClick={() => setMode("upload")}
                        className="text-[11.5px] font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border"
                        style={mode === "upload" ? { borderColor: BRAND, color: BRAND, background: "#FBEFE5" } : { borderColor: "#E5E0DA", color: "#6F6A64" }}>
                        <Upload size={12} /> Upload a document
                    </button>
                </div>
                {mode === "type" ? (
                    <>
                        <textarea data-testid="toolbox-editor-briefing" value={briefing} onChange={(e) => setBriefing(e.target.value)}
                            rows={12} placeholder="e.g. Personal Protective Equipment is the last line of defence..."
                            className="w-full px-3 py-2.5 rounded-lg border text-[13.5px] leading-relaxed" style={{ borderColor: "#E5E0DA" }} />
                        <p className="text-[11px] text-[#9A968F] mt-1.5">Tip: wrap text in **double asterisks** for bold, and start a line with "- " for a bullet point.</p>
                    </>
                ) : (
                    <div className="rounded-lg border p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        {docId ? (
                            <div className="flex items-center justify-between">
                                <span className="text-[13px] text-[#1F1F1F] inline-flex items-center gap-1.5"><FileText size={14} style={{ color: BRAND }} /> {docFilename || "Briefing document uploaded"}</span>
                                <button data-testid="toolbox-editor-remove-doc" onClick={removeDoc} className="text-[11.5px] text-red-500 hover:text-red-700 font-medium">Remove</button>
                            </div>
                        ) : (
                            <label className="inline-flex items-center gap-2 text-[12.5px] font-semibold cursor-pointer" style={{ color: BRAND }}>
                                {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                                {uploading ? "Uploading…" : "Choose a PDF or Word document"}
                                <input data-testid="toolbox-editor-upload-input" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadFile} disabled={uploading} />
                            </label>
                        )}
                        <p className="text-[11px] text-[#9A968F] mt-1.5">Workers see this document inline before they sign — no download required.</p>
                    </div>
                )}
            </div>
            <p className="text-[11px] text-[#9A968F] mb-4">The register itself — name, company, signature, date — is fixed and shown automatically; you only manage the write-up above.</p>

            <div className="flex justify-end gap-2 mt-2 pt-3 border-t" style={{ borderColor: "#F0EDE8" }}>
                <button onClick={closeEditor} className="text-[13px] px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                <button data-testid="toolbox-editor-save" onClick={save} disabled={busy}
                    className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                    {busy ? <Loader2 className="animate-spin" size={14} /> : null} Save
                </button>
            </div>
        </Modal>
    );
}

/* ---------------- Site content ---------------- */
function SitesTab({ clientId }) {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/inductions/sites?client_id=${clientId}`).then((r) => setSites(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, [clientId]);
    useEffect(load, [load]);

    if (loading) return <Loading />;
    if (active) return <SiteBlocksEditor site={active} onBack={() => { setActive(null); load(); }} />;
    return sites.length === 0 ? <Empty msg="This client has no sites yet." /> : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="induction-sites-list">
            {sites.map((s) => (
                <button key={s.id} data-testid={`induction-site-${s.id}`} onClick={() => setActive(s)}
                    className="bg-white border rounded-[13px] p-4 shadow-brand-sm text-left hover:border-[#C75A1B]" style={{ borderColor: "#E5E0DA" }}>
                    <div className="flex items-center gap-2"><Building size={15} style={{ color: BRAND }} /><span className="text-[14px] font-semibold text-[#1F1F1F]">{s.name}</span></div>
                    {s.address && <p className="text-[12px] text-[#9A968F] mt-1 flex items-center gap-1"><MapPin size={11} /> {s.address}</p>}
                    <p className="text-[11.5px] text-[#6F6A64] mt-2">{(s.induction_blocks || []).length} site-specific rule block(s)</p>
                </button>
            ))}
        </div>
    );
}

function SiteBlocksEditor({ site, onBack }) {
    const [blocks, setBlocks] = useState(JSON.parse(JSON.stringify(site.induction_blocks || [])));
    const [assemblyPoints, setAssemblyPoints] = useState(JSON.parse(JSON.stringify(site.assembly_points || [])));
    const [busy, setBusy] = useState(false);
    const [assemblyBusy, setAssemblyBusy] = useState(false);
    const upd = (fn) => setBlocks((prev) => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next; });
    const add = () => upd((b) => b.push({ id: `b${Date.now()}`, heading: "", body: "" }));

    async function save() {
        setBusy(true);
        try {
            await api.put(`/inductions/sites/${site.id}/blocks`, { blocks });
            toast.success("Site rules saved"); onBack();
        } catch { toast.error("Save failed"); }
        setBusy(false);
    }
    async function uploadImage(blockId, file) {
        const fd = new FormData(); fd.append("file", file);
        try {
            const r = await api.post(`/inductions/sites/${site.id}/blocks/${blockId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            upd((b) => { const x = b.find((y) => y.id === blockId); if (x) x.image_path = r.data.image_path; });
            toast.success("Photo added");
        } catch { toast.error("Upload failed"); }
    }
    async function saveAssembly() {
        setAssemblyBusy(true);
        try {
            await api.put(`/inductions/sites/${site.id}/assembly-points`, {
                blocks: assemblyPoints.map((p) => ({ id: p.id, heading: p.heading, body: p.body, image_path: p.image_path })),
            });
            toast.success("Assembly points saved");
        } catch { toast.error("Save failed"); }
        setAssemblyBusy(false);
    }

    return (
        <div className="space-y-3" data-testid="induction-site-blocks-editor">
            <button onClick={onBack} className="text-[12.5px] font-semibold" style={{ color: "#3e4b63" }}>← Back to sites</button>
            <div className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                <h2 className="text-[15px] font-bold text-[#1F1F1F] mb-1">{site.name} — induction rules</h2>
                <p className="text-[12px] text-[#6F6A64] mb-3">These appear at the top of every induction for this site, before the worker signs.</p>
                <div className="space-y-3">
                    {blocks.map((b, i) => (
                        <div key={b.id} data-testid={`site-block-row-${i}`} className="border rounded-xl p-3" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-center gap-2 mb-2">
                                <input value={b.heading} onChange={(e) => upd((x) => { x[i].heading = e.target.value; })} placeholder="Heading (e.g. Smoking area)"
                                    className="flex-1 h-9 px-2.5 rounded-lg border text-[13px] font-semibold" style={{ borderColor: "#E5E0DA" }} />
                                <button onClick={() => upd((x) => x.splice(i, 1))} className="text-red-500"><Trash2 size={15} /></button>
                            </div>
                            <textarea value={b.body || ""} onChange={(e) => upd((x) => { x[i].body = e.target.value; })} rows={2} placeholder="Details / rule"
                                className="w-full px-2.5 py-1.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                            <div className="flex items-center gap-3 mt-2">
                                {b.image_path && <span className="text-[11px] text-green-600 inline-flex items-center gap-1"><ImageIcon size={12} /> Photo attached</span>}
                                <label className="text-[11.5px] font-semibold cursor-pointer inline-flex items-center gap-1" style={{ color: BRAND }}>
                                    <ImageIcon size={13} /> {b.image_path ? "Replace photo" : "Add photo"}
                                    <input data-testid={`site-block-image-${i}`} type="file" accept="image/*" className="hidden"
                                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(b.id, f); }} />
                                </label>
                            </div>
                        </div>
                    ))}
                    <button data-testid="site-block-add" onClick={add} className="text-[12.5px] font-semibold px-3 py-2 rounded-lg border w-full" style={{ borderColor: "#E5E0DA", color: BRAND }}>+ Add rule block</button>
                </div>
                <div className="flex justify-end mt-4">
                    <button data-testid="site-blocks-save" onClick={save} disabled={busy}
                        className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                        {busy ? <Loader2 className="animate-spin" size={14} /> : null} Save rules
                    </button>
                </div>
            </div>

            <div className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }} data-testid="site-assembly-editor">
                <h2 className="text-[15px] font-bold text-[#1F1F1F] mb-1 flex items-center gap-1.5"><MapPin size={15} style={{ color: BRAND }} /> {site.name} — assembly points</h2>
                <p className="text-[12px] text-[#6F6A64] mb-3">Where everyone should gather in an emergency. Shown on every induction for this site.</p>
                <AssemblyPointsEditor siteId={site.id} points={assemblyPoints} setPoints={setAssemblyPoints} brand={BRAND} testidPrefix="site-assembly" />
                <div className="flex justify-end mt-4">
                    <button data-testid="site-assembly-save" onClick={saveAssembly} disabled={assemblyBusy}
                        className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                        {assemblyBusy ? <Loader2 className="animate-spin" size={14} /> : null} Save assembly points
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Sign-on & attendance ---------------- */
function QrTab({ clientId }) {
    const [sites, setSites] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [templateId, setTemplateId] = useState("");

    useEffect(() => {
        api.get(`/inductions/sites?client_id=${clientId}`).then((r) => { setSites(r.data || []); if ((r.data || [])[0]) setSiteId(r.data[0].id); }).catch(() => {});
        api.get(`/inductions/templates?client_id=${clientId}`).then((r) => {
            setTemplates(r.data || []);
            const def = (r.data || []).find((t) => t.is_default) || (r.data || [])[0];
            if (def) setTemplateId(def.id);
        }).catch(() => {});
    }, [clientId]);

    const site = sites.find((s) => s.id === siteId);
    const tpl = templates.find((t) => t.id === templateId);
    return (
        <div className="space-y-4" data-testid="induction-qr-tab">
            <div className="flex flex-wrap gap-3">
                <label className="text-[12px] font-semibold text-[#1F1F1F]">Site
                    <select data-testid="induction-qr-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="ml-2 h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                        <option value="">— Select —</option>
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </label>
                <label className="text-[12px] font-semibold text-[#1F1F1F]">Induction template
                    <select data-testid="induction-qr-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="ml-2 h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </label>
            </div>
            {tpl?.description && <p data-testid="induction-qr-template-desc" className="text-[12px] text-[#6F6A64] -mt-1">{tpl.description}</p>}
            {!siteId ? <Empty msg="Select a site to generate its QR posters." /> : (
                <div className="grid lg:grid-cols-2 gap-4">
                    <SiteQRPoster clientId={clientId} siteId={siteId} siteName={site?.name}
                        templateId={templateId || undefined} templateCode={tpl?.code || "SITE-INDUCTION"} templateLabel={tpl?.name || "Site Induction"} />
                    <SiteQRPoster clientId={clientId} siteId={siteId} siteName={site?.name} mode="attendance" />
                    <div className="lg:col-span-2"><AttendanceViewer clientId={clientId} siteId={siteId} /></div>
                </div>
            )}
        </div>
    );
}

function AttendanceViewer({ clientId, siteId }) {
    const [rows, setRows] = useState([]);
    const [date, setDate] = useState("");
    const load = useCallback(() => {
        const p = new URLSearchParams({ site_id: siteId });
        if (date) p.set("date", date);
        api.get(`/site-forms/attendance?${p.toString()}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
    }, [siteId, date]);
    useEffect(load, [load]);
    const onSite = rows.filter((r) => !r.time_out);
    const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
    return (
        <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="induction-attendance-viewer">
            <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                <QrCode size={15} style={{ color: BRAND }} />
                <h3 className="text-[13.5px] font-semibold flex-1">On site now ({onSite.length})</h3>
                <input type="date" data-testid="induction-attendance-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
            </div>
            <div className="p-4">
                {rows.length === 0 ? <p className="text-[12px] text-[#9A968F] text-center py-2">No attendance recorded.</p> : (
                    <ul className="space-y-1" data-testid="induction-attendance-list">
                        {rows.map((r) => (
                            <li key={r.id} className="flex items-center gap-2 text-[12px] py-1 border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                {!r.time_out && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                                <span className="font-medium text-[#1F1F1F]">{r.name}</span>
                                <span className="text-[#9A968F] truncate">{r.company ? `· ${r.company}` : ""}</span>
                                <span className="ml-auto text-[11px] text-[#6F6A64] whitespace-nowrap">{fmt(r.time_in)} → {r.time_out ? fmt(r.time_out) : <span className="text-green-600 font-semibold">on site</span>}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/* ---------------- Records & metrics ---------------- */
function RecordsTab({ clientId, deepLinkFilter }) {
    const navigate = useNavigate();
    const [inductions, setInductions] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("inductions");   // inductions | attendance

    const load = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams({ client_id: clientId });
        if (deepLinkFilter?.projectId) p.set("project_id", deepLinkFilter.projectId);
        if (deepLinkFilter?.dateFrom) p.set("date_from", deepLinkFilter.dateFrom);
        if (deepLinkFilter?.dateTo) p.set("date_to", deepLinkFilter.dateTo);
        Promise.all([
            api.get(`/inductions/records?${p.toString()}`),
            api.get(`/site-forms/attendance?client_id=${clientId}`),
        ]).then(([r, a]) => { setInductions(r.data || []); setAttendance(a.data || []); })
            .catch(() => {}).finally(() => setLoading(false));
    }, [clientId, deepLinkFilter]);
    useEffect(load, [load]);

    const now = new Date(); const weekAgo = new Date(now - 7 * 864e5).toISOString();
    const thisWeek = inductions.filter((r) => (r.submitted_at || "") >= weekAgo).length;
    const outstanding = inductions.filter((r) => r.evidence_status === "outstanding").length;
    const onSite = attendance.filter((r) => !r.time_out).length;
    const companies = new Set(inductions.map((r) => r.company).filter(Boolean)).size;
    const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s || "—"; } };

    if (loading) return <Loading />;
    return (
        <div className="space-y-4" data-testid="induction-records-tab">
            {deepLinkFilter?.projectId && (
                <div className="text-[12px] font-semibold px-3 py-2 rounded-lg" style={{ background: "#FBF3EC", color: "#C75A1B" }} data-testid="induction-records-deep-link-filter">
                    Filtered from a Weekly Site Report — showing this project's reporting week only.
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Metric testid="metric-total-inductions" label="Inductions" value={inductions.length} icon={ShieldCheck} />
                <Metric testid="metric-week-inductions" label="This week" value={thisWeek} icon={Star} />
                <Metric testid="metric-evidence-outstanding" label="Evidence outstanding" value={outstanding} icon={AlertTriangle} tone={outstanding ? "danger" : "ok"} />
                <Metric testid="metric-on-site-now" label="On site now" value={onSite} icon={QrCode} tone={onSite ? "green" : "muted"} />
                <Metric testid="metric-companies" label="Companies" value={companies} icon={Building} />
            </div>

            <div className="flex items-center gap-1 border-b" style={{ borderColor: "#E5E0DA" }}>
                {[["inductions", `Inductions (${inductions.length})`], ["attendance", `Site visits (${attendance.length})`]].map(([id, label]) => (
                    <button key={id} data-testid={`records-view-${id}`} onClick={() => setView(id)}
                        className="px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px"
                        style={view === id ? { borderColor: BRAND, color: BRAND } : { borderColor: "transparent", color: "#6F6A64" }}>{label}</button>
                ))}
                <button data-testid="records-refresh" onClick={load} className="ml-auto text-[#9A968F] hover:text-[#1F1F1F] pb-1.5" title="Refresh"><RefreshCw size={14} /></button>
            </div>

            {view === "inductions" ? (
                inductions.length === 0 ? <Empty msg="No inductions completed yet." /> : (
                    <div className="bg-white border rounded-[13px] overflow-hidden shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                        <table className="w-full text-[12.5px]" data-testid="induction-records-table">
                            <thead><tr className="text-left text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                <th className="px-4 py-2.5 font-semibold">Name</th><th className="px-4 py-2.5 font-semibold">Company</th>
                                <th className="px-4 py-2.5 font-semibold">Induction</th><th className="px-4 py-2.5 font-semibold">Site</th>
                                <th className="px-4 py-2.5 font-semibold">Completed</th><th className="px-4 py-2.5 font-semibold">Evidence</th>
                            </tr></thead>
                            <tbody>
                                {inductions.map((r) => (
                                    <tr key={r.id} data-testid={`induction-record-${r.id}`} onClick={() => navigate(`/app/inspections/${r.id}`)}
                                        className="border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F5F3F0" }}>
                                        <td className="px-4 py-2.5 font-semibold text-[#1F1F1F]">{r.name || "—"}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{r.company || "—"}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{r.template_name}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{r.site_name || "—"}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{fmt(r.submitted_at)}</td>
                                        <td className="px-4 py-2.5">
                                            {r.evidence_status === "outstanding"
                                                ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FBE9E7", color: "#B33A2E" }}>Outstanding</span>
                                                : r.evidence_status === "complete"
                                                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#EAF3EC", color: "#2E7D4F" }}>Complete</span>
                                                    : <span className="text-[#9A968F]">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                attendance.length === 0 ? <Empty msg="No site visits recorded yet." /> : (
                    <div className="bg-white border rounded-[13px] overflow-hidden shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                        <table className="w-full text-[12.5px]" data-testid="attendance-records-table">
                            <thead><tr className="text-left text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                <th className="px-4 py-2.5 font-semibold">Name</th><th className="px-4 py-2.5 font-semibold">Company</th>
                                <th className="px-4 py-2.5 font-semibold">Signed in</th><th className="px-4 py-2.5 font-semibold">Signed out</th>
                            </tr></thead>
                            <tbody>
                                {attendance.map((r) => (
                                    <tr key={r.id} data-testid={`attendance-record-${r.id}`} className="border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                        <td className="px-4 py-2.5 font-semibold text-[#1F1F1F]">{r.name}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{r.company || "—"}</td>
                                        <td className="px-4 py-2.5 text-[#6F6A64]">{fmt(r.time_in)}</td>
                                        <td className="px-4 py-2.5">{r.time_out ? <span className="text-[#6F6A64]">{fmt(r.time_out)}</span> : <span className="text-green-600 font-semibold text-[11px]">On site</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}

function Metric({ testid, label, value, icon: Icon, tone }) {
    const colours = { danger: "#B33A2E", green: "#2E7D4F", ok: "#6F6A64", muted: "#9A968F" };
    const col = colours[tone] || BRAND;
    return (
        <div data-testid={testid} className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#9A968F" }}><Icon size={13} style={{ color: col }} /> {label}</div>
            <div className="text-2xl font-bold mt-1" style={{ color: col }}>{value}</div>
        </div>
    );
}

/* ---------------- Toolbox talk — Beyond AI import ---------------- */
function renderInline(text, keyPrefix) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
        part.startsWith("**") && part.endsWith("**")
            ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
            : <span key={`${keyPrefix}-${i}`}>{part}</span>
    ));
}
function renderBriefing(text) {
    if (!text) return null;
    const nodes = [];
    let bullets = [];
    const flush = () => {
        if (bullets.length) {
            nodes.push(<ul key={`ul-${nodes.length}`} className="list-disc pl-5 space-y-1">
                {bullets.map((l, i) => <li key={i}>{renderInline(l, `b-${nodes.length}-${i}`)}</li>)}
            </ul>);
            bullets = [];
        }
    };
    text.split("\n").forEach((raw, i) => {
        const line = raw.trim();
        if (!line) { flush(); return; }
        if (line.startsWith("- ") || line.startsWith("* ")) { bullets.push(line.slice(2)); return; }
        flush();
        nodes.push(<p key={`p-${i}`}>{renderInline(line, `p-${i}`)}</p>);
    });
    flush();
    return nodes;
}

function ToolboxAiImportDialog({ clientId, onClose, onImported }) {
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState("file");
    const [file, setFile] = useState(null);
    const [textInput, setTextInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    async function analyse() {
        if (mode === "file" && !file) { toast.error("Please choose a file"); return; }
        if (mode === "text" && !textInput.trim()) { toast.error("Please paste some text"); return; }
        setLoading(true); setError(null); setStep(2);
        try {
            const fd = new FormData();
            if (mode === "file") fd.append("file", file); else fd.append("text_input", textInput);
            fd.append("client_id", clientId);
            const { data } = await api.post("/toolbox/templates/ai-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(data); setStep(3);
        } catch (e) {
            setError(e.response?.data?.detail || "Beyond AI couldn't draft a briefing from this document. Please try again.");
            setStep(1);
        } finally { setLoading(false); }
    }

    async function discard() {
        if (result?.template?.id) { try { await api.delete(`/toolbox/templates/${result.template.id}`); } catch { /* best effort */ } }
        setResult(null); setFile(null); setTextInput(""); setStep(1);
    }

    async function confirm() {
        setBusy(true);
        try {
            await api.patch(`/toolbox/templates/${result.template.id}`, { status: "published" });
            toast.success(`"${result.template.name}" added to Toolbox Talks`);
            onImported();
        } catch { toast.error("Failed to publish"); }
        finally { setBusy(false); }
    }

    return (
        <Modal title="Import toolbox talk with Beyond AI" onClose={onClose} wide testid="toolbox-ai-import-dialog">
            {step === 1 && (
                <div className="space-y-4">
                    <p className="text-[12.5px] text-[#6F6A64]">Upload a document (e.g. one of your existing toolbox talks) — Beyond AI drafts a ready-to-use briefing for you to preview before it's added.</p>
                    {error && <p data-testid="toolbox-ai-import-error" className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>}
                    <div className="flex gap-2 p-1 bg-[#F0EDE8] rounded-xl w-fit">
                        {[["file", "Upload file"], ["text", "Paste text"]].map(([m, label]) => (
                            <button key={m} onClick={() => setMode(m)} data-testid={`toolbox-ai-import-mode-${m}`}
                                className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold transition-all ${mode === m ? "bg-white shadow-sm text-[#1F1F1F]" : "text-[#6F6A64]"}`}>{label}</button>
                        ))}
                    </div>
                    {mode === "file" ? (
                        <label data-testid="toolbox-ai-import-dropzone"
                            className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${file ? "border-emerald-300 bg-emerald-50/40" : "border-[#D8D3CB] hover:border-[#9A968F] bg-white"}`}>
                            <input type="file" accept=".docx,.doc,.pdf,.txt" className="sr-only" data-testid="toolbox-ai-import-file"
                                onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            {file ? (
                                <p className="text-[13px] font-semibold text-[#1F1F1F]">{file.name}</p>
                            ) : (
                                <>
                                    <Upload size={24} className="mx-auto mb-2 text-[#C4BEB4]" />
                                    <p className="text-[13px] font-semibold text-[#1F1F1F]">Drop file here or click to browse</p>
                                    <p className="text-[11.5px] text-[#9A968F] mt-1">Supports: .docx, .doc, .pdf, .txt</p>
                                </>
                            )}
                        </label>
                    ) : (
                        <textarea data-testid="toolbox-ai-import-text" value={textInput} rows={8} onChange={(e) => setTextInput(e.target.value)}
                            placeholder="Paste the toolbox talk text here…"
                            className="w-full p-3 rounded-xl border text-[13px] font-mono" style={{ borderColor: "#E5E0DA" }} />
                    )}
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="text-[13px] px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                        <button data-testid="toolbox-ai-import-analyse" onClick={analyse}
                            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5 bg-ai-grad">
                            <Sparkles size={14} /> Analyse with Beyond AI
                        </button>
                    </div>
                </div>
            )}
            {step === 2 && (
                <div className="text-center py-10" data-testid="toolbox-ai-import-loading">
                    <Loader2 className="animate-spin mx-auto mb-3" size={28} style={{ color: BRAND }} />
                    <p className="text-[13px] text-[#6F6A64]">{loading ? "Beyond AI is drafting the briefing…" : "…"}</p>
                </div>
            )}
            {step === 3 && result && (
                <div className="space-y-4" data-testid="toolbox-ai-import-result">
                    <div>
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[#9A968F]">Talk name</span>
                        <p className="text-[15px] font-bold text-[#1F1F1F]">{result.template.name}</p>
                    </div>
                    <div className="rounded-xl border p-4 max-h-64 overflow-y-auto" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }} data-testid="toolbox-ai-import-preview">
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[#9A968F]">Briefing preview — shown to attendees before they sign</span>
                        <div className="text-[13px] text-[#1F1F1F] leading-relaxed space-y-2 mt-2">{renderBriefing(result.template.briefing_body)}</div>
                    </div>
                    <p className="text-[11.5px] text-[#9A968F]">This talk has been saved as a draft — it won't appear active in your Toolbox Talks list until you confirm below.</p>
                    <div className="flex justify-end gap-2">
                        <button data-testid="toolbox-ai-import-discard" onClick={discard}
                            className="text-[13px] px-3 py-2 rounded-lg border inline-flex items-center gap-1.5" style={{ borderColor: "#E5E0DA" }}>
                            <RefreshCw size={13} /> Discard &amp; try again
                        </button>
                        <button data-testid="toolbox-ai-import-confirm" onClick={confirm} disabled={busy}
                            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                            {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle size={14} />} Add to Toolbox Talks
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

/* ---------------- shared bits ---------------- */
function TBtn({ testid, icon: Icon, label, onClick, danger }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium"
            style={{ borderColor: "#E5E0DA", color: danger ? "#B33A2E" : "#6F6A64" }}>
            <Icon size={12} /> {label}
        </button>
    );
}
function Modal({ title, children, onClose, wide, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid={testid}>
            <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} p-5`} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[15px] font-bold text-[#1F1F1F]">{title}</h3>
                    <button onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
function Loading() { return <div className="text-center py-16"><Loader2 className="animate-spin inline" size={22} /></div>; }
function Empty({ msg }) { return <div className="text-center text-[13px] text-[#9A968F] py-16 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>{msg}</div>; }
